import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, FormControlLabel, MenuItem, TextField, Typography,
} from '@mui/material';
import { fieldSxFromTheme, getPortalTheme } from '../portalTheme';

const CHELEK_OPTIONS = ['אורח חיים', 'יורה דעה', 'אבן העזר', 'חושן משפט'];

const TYPE_LABELS = {
  time: 'זמן',
  shimur: 'שימור',
  miktzoa: 'מקצוע',
};

function chelekLabel(chelekim) {
  if (!chelekim?.length) return 'לא הוגדר';
  return chelekim.join(', ');
}

const HalachaExamsStep = ({
  openExams = [],
  activePeriod,
  selectedExamIds = [],
  setSelectedExamIds,
  onBack,
  onNext,
  theme,
}) => {
  const t = theme || getPortalTheme(true);
  const fieldSx = fieldSxFromTheme(t);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [chelekFilter, setChelekFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');

  const subjects = useMemo(() => {
    const set = new Set();
    for (const e of openExams) {
      const s = String(e.subject || '').trim();
      if (s) set.add(s);
    }
    return [...set].sort((a, b) => a.localeCompare(b, 'he'));
  }, [openExams]);

  const filteredExams = useMemo(() => {
    const q = search.trim().toLowerCase();
    return openExams.filter((exam) => {
      if (chelekFilter) {
        const chelekim = exam.chelekim || [];
        if (!chelekim.includes(chelekFilter)) return false;
      }
      if (subjectFilter) {
        if (String(exam.subject || '').trim() !== subjectFilter) return false;
      }
      if (!q) return true;
      const hay = [
        exam.code,
        exam.name,
        exam.subject,
        exam.simanimText,
        ...(exam.chelekim || []),
        TYPE_LABELS[exam.type] || exam.type,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [openExams, search, chelekFilter, subjectFilter]);

  const toggleExam = (id) => {
    setSelectedExamIds((prev) => {
      const set = new Set(prev);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return [...set];
    });
  };

  const handleContinue = () => {
    setError('');
    if (!activePeriod) {
      setError('אין זמן מבחנים פעיל. לא ניתן להירשם כרגע.');
      return;
    }
    if (!openExams.length) {
      setError('אין מבחנים פתוחים להרשמה בתקופה הפעילה.');
      return;
    }
    if (!selectedExamIds.length) {
      setError('יש לבחור לפחות מבחן אחד');
      return;
    }
    onNext();
  };

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto' }}>
      <Typography
        variant="h5"
        sx={{
          color: '#f5f0e6',
          fontFamily: '"Frank Ruhl Libre", "David Libre", serif',
          fontWeight: 700,
          mb: 1,
          textAlign: 'center',
        }}
      >
        בחירת מבחנים
      </Typography>
      <Typography sx={{ color: 'rgba(245,240,230,0.75)', mb: 2.5, textAlign: 'center', fontSize: '0.95rem', lineHeight: 1.8 }}>
        סמנו את המבחנים אליהם תרצו להירשם
        {activePeriod?.name ? ` למועד ${activePeriod.name}` : ''}.
        ניתן לבחור מספר מבחנים.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      {openExams.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.5 }}>
          <TextField
            fullWidth
            size="small"
            label="חיפוש"
            placeholder="קוד, שם, מקצוע או חלק..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={fieldSx}
          />
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="חלק"
              value={chelekFilter}
              onChange={(e) => setChelekFilter(e.target.value)}
              sx={fieldSx}
            >
              <MenuItem value="">הכל</MenuItem>
              {CHELEK_OPTIONS.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              size="small"
              label="מקצוע"
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
              sx={fieldSx}
            >
              <MenuItem value="">הכל</MenuItem>
              {subjects.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>
      )}

      {!openExams.length ? (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          אין מבחנים פתוחים להרשמה בתקופה הפעילה. פנו למשרד.
        </Alert>
      ) : !filteredExams.length ? (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          לא נמצאו מבחנים התואמים לחיפוש. נסו לשנות חלק או מקצוע.
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, mb: 3 }}>
          {filteredExams.map((exam) => {
            const id = String(exam.id);
            const checked = selectedExamIds.includes(id);
            const typeLabel = TYPE_LABELS[exam.type] || exam.type || '';
            const simanimLine = exam.simanimText?.trim()
              || 'סימנים: לא הוגדרו';
            return (
              <Box
                key={id}
                onClick={() => toggleExam(id)}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  px: 1.5,
                  py: 1.3,
                  borderRadius: 2,
                  cursor: 'pointer',
                  border: checked
                    ? `1px solid ${t.accent}`
                    : `1px solid ${t.accentBorderSoft}`,
                  background: checked ? t.surfaceSelected : 'rgba(255,255,255,0.03)',
                  transition: 'border-color 0.15s, background 0.15s',
                }}
              >
                <FormControlLabel
                  onClick={(e) => e.stopPropagation()}
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={() => toggleExam(id)}
                      sx={{
                        color: t.checkboxIdle,
                        '&.Mui-checked': { color: t.checkbox },
                      }}
                    />
                  }
                  label=""
                  sx={{ m: 0, mr: 0.5 }}
                />
                <Box sx={{ flex: 1, minWidth: 0, pt: 0.4 }}>
                  <Typography sx={{ color: '#f5f0e6', fontWeight: 700, fontSize: '0.98rem' }}>
                    {exam.code != null ? `${exam.code} · ` : ''}
                    {exam.name || 'מבחן'}
                  </Typography>
                  <Typography sx={{ color: t.accent, fontWeight: 700, fontSize: '0.88rem', mt: 0.55, lineHeight: 1.55 }}>
                    חלק: {chelekLabel(exam.chelekim)}
                    {' · '}
                    מקצוע: {exam.subject?.trim() || 'לא הוגדר'}
                  </Typography>
                  <Typography sx={{ color: 'rgba(245,240,230,0.6)', fontSize: '0.82rem', mt: 0.35 }}>
                    {[typeLabel, simanimLine].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {selectedExamIds.length > 0 && (
        <Typography sx={{ color: t.accent, textAlign: 'center', mb: 2, fontWeight: 700 }}>
          נבחרו {selectedExamIds.length} מבחנים
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1.5 }}>
        <Button
          onClick={onBack}
          sx={{
            flex: 1,
            minHeight: 48,
            py: 1.2,
            borderRadius: 2,
            color: 'rgba(245,240,230,0.85)',
            border: `1px solid ${t.accentBorder}`,
          }}
        >
          חזרה
        </Button>
        <Button
          onClick={handleContinue}
          sx={{
            flex: 2,
            minHeight: 48,
            py: 1.2,
            fontWeight: 700,
            borderRadius: 2,
            color: t.ctaColor,
            background: t.ctaBg,
            '&:hover': { filter: 'brightness(1.06)' },
          }}
        >
          המשך
        </Button>
      </Box>
    </Box>
  );
};

export default HalachaExamsStep;
